import { User as UserIcon, Wallet, BadgePercent, Building2, Landmark, DollarSign, Sun, Palmtree } from 'lucide-react';
import { BRAND } from '../kit/tokens';

// Manual calculator types — merged W-2 into single "Wage Earner" (auto-handles salary/hourly/OT/bonus)
export const types = [
  { k: 'w2_wage', l: 'W-2 Wage Earner', icon: UserIcon },
  { k: 'self_employed_sch_c', l: 'Self-Employed (Sch C)', icon: Wallet },
  { k: 'k1', l: 'K-1 (Partnership/S-Corp)', icon: BadgePercent },
  { k: 'rental', l: 'Rental Income', icon: Building2 },
  { k: 'retirement', l: 'Retirement / SS / Pension', icon: Landmark },
  { k: 'bank_statement', l: 'Bank Statement', icon: DollarSign },
];

export const fmt$ = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// Program Bakeoff tax years: the latest completed tax year (last calendar
// year) and the three before it, newest first, as PickSelect options.
export const latestCompletedTaxYear = (now = new Date()) => now.getFullYear() - 1;
export const bakeoffYearOptions = (now = new Date()) => {
  const latest = latestCompletedTaxYear(now);
  return [0, 1, 2, 3].map((i) => ({ value: String(latest - i), label: String(latest - i) }));
};

// Program Bakeoff inputs beyond Schedule C (ticket 199). Field names are the
// Form91 request fields; each `key` is the year-level array they go into.
export const BAKEOFF_ROYALTY_FIELDS = [
  { field: 'royalties_received', label: 'Royalties Received (L4)' },
  { field: 'royalties_total_expenses', label: 'Royalty Expenses (L20)' },
  { field: 'royalties_depletion', label: 'Royalty Depletion (L18)' },
];
export const BAKEOFF_K1_FIELDS = [
  { field: 'ordinary_business_income', label: 'Ordinary Income (L1)' },
  { field: 'net_rental_real_estate', label: 'Net Rental RE (L2-3)' },
  { field: 'guaranteed_payments', label: 'Guaranteed Pmts (L4c)', partnershipOnly: true },
];
const COMMON_BUSINESS_FIELDS = [
  { field: 'depletion', label: 'Depletion' },
  { field: 'amortization_casualty', label: 'Amort/Cas' },
  { field: 'mortgage_notes_lt1yr', label: 'Notes Payable <1yr (Sch L)' },
  { field: 'other_nonrecurring', label: 'Other Nonrecurring' },
  { field: 'travel_ent_exclusion', label: 'Travel/Ent Exclusion (M-1)' },
];
export const BAKEOFF_BUSINESS_RETURNS = {
  '1065': {
    label: 'Form 1065 (Partnership)',
    key: 'form_1065s',
    fields: [
      { field: 'depreciation_1065', label: 'Depreciation (L16c)' },
      { field: 'depreciation_8825', label: 'Depreciation (8825)' },
      ...COMMON_BUSINESS_FIELDS,
    ],
  },
  '1120s': {
    label: 'Form 1120S (S-Corp)',
    key: 'form_1120ss',
    fields: [
      { field: 'depreciation_1120s', label: 'Depreciation (L14)' },
      { field: 'depreciation_8825', label: 'Depreciation (8825)' },
      ...COMMON_BUSINESS_FIELDS,
    ],
  },
  '1120': {
    label: 'Form 1120 (C-Corp)',
    key: 'form_1120s',
    fields: [
      { field: 'taxable_income', label: 'Taxable Income (L30)' },
      { field: 'total_tax', label: 'Total Tax (L31)' },
      { field: 'depreciation', label: 'Depreciation (L20)' },
      { field: 'net_operating_loss', label: 'Net Operating Loss (L29c)' },
      ...COMMON_BUSINESS_FIELDS,
    ],
  },
};

// Ticket 139: a calc refused for a missing required field has qualifying_monthly null.
export const isIncomplete = (calc) => calc != null && calc.qualifying_monthly == null;
export const missingLabels = (calc) => (calc?.missing_fields || []).map((m) => m.label).join(', ');
// Tickets 239 + 245: a falling year is refused with kind "declining", not a blank field.
export const isDeclining = (m) => m?.kind === 'declining';
// The refusal line for a calc with no number: declining labels first, then "missing ...".
export const refusalText = (calc, lead = "Can't calculate") => {
  const entries = calc?.missing_fields || [];
  const declining = entries.filter(isDeclining).map((m) => m.label);
  const absent = entries.filter((m) => !isDeclining(m)).map((m) => m.label);
  const parts = [...declining];
  if (absent.length || !declining.length) parts.push(`missing ${absent.join(', ') || 'a required field'}`);
  return `${lead} — ${parts.join('; ')}.`;
};

// FastAPI/pydantic error `detail` can be a string, a list of validation error
// dicts (e.g. EmailStr rejecting a malformed address), or a structured object
// — normalize whatever shape it takes into a displayable string.
export const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => x?.msg).filter(Boolean).join('; ') || fallback;
  if (d && typeof d === 'object') return d.message || d.reason || d.code || fallback;
  return fallback;
};

// Category → display label / icon / whether it's base income
export const CAT_META = {
  regular: { label: 'Regular / Base', base: true },
  salary: { label: 'Salary', base: true },
  holiday: { label: 'Holiday Pay', base: true, icon: Sun },
  pto: { label: 'PTO', base: true, icon: Palmtree },
  vacation: { label: 'Vacation', base: true, icon: Palmtree },
  sick: { label: 'Sick Pay', base: true },
  personal: { label: 'Personal Time', base: true },
  bereavement: { label: 'Bereavement', base: true },
  jury_duty: { label: 'Jury Duty', base: true },
  floating_holiday: { label: 'Floating Holiday', base: true },
  overtime: { label: 'Overtime', base: false },
  bonus: { label: 'Bonus', base: false },
  commission: { label: 'Commission', base: false },
  shift_differential: { label: 'Shift Differential', base: false },
  tips: { label: 'Tips', base: false },
  incentive: { label: 'Incentive', base: false },
  production: { label: 'Production', base: false },
  hazard: { label: 'Hazard Pay', base: false },
  on_call: { label: 'On-Call', base: false },
  standby: { label: 'Standby', base: false },
  longevity: { label: 'Longevity', base: false },
  retro: { label: 'Retro Pay', base: false },
  other: { label: 'Other', base: false },
};

// Brand gradients shared across the income-calculator panels (JS-land, so
// they use BRAND tokens rather than Tailwind classes).
export const NAVY_GRADIENT = `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.navyLight} 100%)`;
export const NAVY_GRADIENT_STEEP = `linear-gradient(160deg, ${BRAND.navy} 0%, ${BRAND.navyLight} 100%)`;
export const ACCENT_BAR_GRADIENT = `linear-gradient(90deg, ${BRAND.accent} 0%, ${BRAND.accentBright} 60%, ${BRAND.accent} 100%)`;
export const ACCENT_BAR_GRADIENT_SOFT = `linear-gradient(90deg, ${BRAND.accent} 0%, ${BRAND.accentSoft} 60%, ${BRAND.accent} 100%)`;
